import { ISDKConfig } from './types';

export const API_PREFIX = '/api/v1';

export class SDKConfig implements ISDKConfig {
  api_protocol: string;
  api_host: string;
  api_port: number;
  deploy_env: string;
  auth_provider: string;
  flowpad_app_host: string;
  flowpad_app_port?: number;
  check_refresh_token: boolean;
  ui_version: string;

  constructor(config: ISDKConfig) {
    this.api_protocol = config.api_protocol;
    this.api_host = config.api_host;
    this.api_port = config.api_port;
    this.deploy_env = config.deploy_env;
    this.auth_provider = config.auth_provider;
    this.flowpad_app_host = config.flowpad_app_host;
    this.flowpad_app_port = config.flowpad_app_port;
    this.check_refresh_token = config.check_refresh_token;
    this.ui_version = config.ui_version;
  }

  private needsPortInUrl(): boolean {
    return !(
      (this.api_protocol === 'http' && this.api_port === 80) ||
      (this.api_protocol === 'https' && this.api_port === 443)
    );
  }

  get apiUrl(): string {
    const portSuffix = this.needsPortInUrl() ? `:${this.api_port}` : '';
    return `${this.api_protocol}://${this.api_host}${portSuffix}`;
  }

  get serverUrl(): string {
    return `${this.apiUrl}${API_PREFIX}`;
  }

  /**
   * HTTP base URL for the axios client (the single base consumer).
   *
   * In local dev the FE (Vite dev server) and BE run on different ports, so an
   * absolute `serverUrl` makes every non-simple POST cross-origin and pays a
   * CORS preflight OPTIONS round-trip. Returning the RELATIVE `/api/v1` prefix
   * keeps HTTP requests same-origin — Vite's `server.proxy` forwards them to the
   * backend (the pattern already proven by `dep_graph`) — eliminating preflights.
   *
   * This relaxation is dev-only. Packaged/non-local builds (`deploy_env` other
   * than `local`) and Electron/realm builds (which pin an absolute backend via
   * `globalThis.__FLOWPAD_API_URL__`) keep the absolute `serverUrl`. `wsUrl`,
   * `apiUrl` and `serverUrl` are intentionally left untouched.
   */
  get httpBaseUrl(): string {
    const hasRuntimeOverride =
      typeof globalThis !== 'undefined' &&
      typeof (globalThis as { __FLOWPAD_API_URL__?: unknown }).__FLOWPAD_API_URL__ === 'string';
    return this.isLocal && this.api_host === 'localhost' && !hasRuntimeOverride ? API_PREFIX : this.serverUrl;
  }

  get wsUrl(): string {
    const wsProtocol = this.api_protocol === 'https' ? 'wss' : 'ws';
    const portSuffix = this.needsPortInUrl() ? `:${this.api_port}` : '';
    return `${wsProtocol}://${this.api_host}${portSuffix}${API_PREFIX}/connect/ws`;
  }

  get isDevelopment(): boolean {
    return this.deploy_env.toLowerCase() === 'development';
  }

  get isLocal(): boolean {
    return this.deploy_env.toLowerCase() === 'local';
  }

  get isProduction(): boolean {
    return this.deploy_env.toLowerCase() === 'production';
  }

  // Legacy compatibility properties
  get SERVER_URL(): string {
    return this.serverUrl;
  }

  get API_PREFIXES() {
    return {
      bootstrap: '/graph/bootstrap',
      refreshToken: '/cloud/refresh-token',
      graph: '/graph',
      currentUser: '/current-user',
      login: '/login',
      signup: '/signup',
      logout: '/logout',
      connect: '/connect/ws',
      health: '/health/status',
      redirect: '/redirect',
      schema: '/public_schema',
    };
  }

  get apiPrefixes() {
    return this.API_PREFIXES;
  }

  get FLOWPAD_APP_HOST(): string {
    return this.flowpad_app_host;
  }

  get FLOWPAD_APP_PORT(): number | undefined {
    return this.flowpad_app_port;
  }
}
